import React from 'react'
import { useList, useInvalidate } from '@refinedev/core'
import { App as AntdApp, Button } from 'antd'
import { brand, statusColor } from '../../theme/brand'
import { ListPage, SectionCard, Pill, EmptyHint, type Stat } from '../../theme/listkit'
import { Bell, Send, UsersRound } from '../../components/icons'
import { supabaseClient } from '../../lib/supabaseClient'

interface PushBroadcast {
  id: string
  title: string
  body: string
  audience: 'all' | 'class_year' | 'admins'
  class_year: string | null
  recipient_count: number | null
  status: 'sent' | 'failed'
  sent_by: string | null
  created_at: string
}

/** Render the audience column in human-readable form. */
export function fmtAudience(row: Pick<PushBroadcast, 'audience' | 'class_year'>): string {
  if (row.audience === 'class_year' && row.class_year != null) {
    return `Class of ${row.class_year}`
  }
  if (row.audience === 'admins') return 'Admins'
  return 'All members'
}

const fmtWhen = (v: string | null) =>
  v
    ? new Date(v).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '—'

const truncate = (s: string, max = 80) =>
  s.length > max ? s.slice(0, max - 1) + '…' : s

const mono: React.CSSProperties = {
  fontFamily: brand.fSans,
  fontWeight: 700,
  fontSize: 13,
  color: brand.ink,
}

/**
 * Push Broadcasts Sent History — read-only log of every push notification that
 * was dispatched via the send-push Edge Function, newest first. Uses liveMode
 * 'off' (single subscription, matching the moderation_actions pattern) to avoid
 * a double subscription when the Refine global liveMode is 'auto'.
 */
interface PushBroadcastsSummary {
  id: number
  total_broadcasts: number | null
  sent_count: number | null
  failed_count: number | null
  total_recipients: number | null
}

export function PushBroadcastsList() {
  // History list: BOUNDED pageSize (the log is small and read-only; 200 >> the
  // realistic broadcast count) so it never issues an unbounded fetch.
  const { result } = useList<PushBroadcast>({
    resource: 'push_broadcasts',
    pagination: { pageSize: 200 },
    liveMode: 'off',
    sorters: [{ field: 'created_at', order: 'desc' }],
  })
  const rows = result?.data ?? []

  // Deck numbers (incl. a SUM of recipient_count) come from the one-row
  // admin_push_broadcasts_summary VIEW — computed in Postgres, read with a
  // bounded pageSize:1 query — instead of summing the whole table client-side.
  const { result: summaryRes } = useList<PushBroadcastsSummary>({
    resource: 'admin_push_broadcasts_summary',
    pagination: { pageSize: 1 },
    liveMode: 'off',
  })
  const summary = summaryRes?.data?.[0]
  const totalBroadcasts = summary?.total_broadcasts ?? 0
  const sentCount = summary?.sent_count ?? 0
  const totalRecipients = summary?.total_recipients ?? 0

  // Clear history. Destructive and irreversible from this screen, so it asks
  // first and says exactly how many rows will go. The rows are NOT the audit
  // record — push_broadcasts carries an admin_audit trigger that fires per row
  // on DELETE, so every removal is hash-chained into admin_audit_log. The
  // confirmation says so, because "clear the history" sounds like it destroys
  // the trail and it doesn't.
  const invalidate = useInvalidate()
  // `modal` from useApp(), not the static Modal.confirm: the static API renders
  // outside the App/ConfigProvider context, so it loses the admin theme and, on
  // React 19, does not reliably mount at all.
  const { message, modal } = AntdApp.useApp()
  const [clearing, setClearing] = React.useState(false)

  const clearHistory = () => {
    modal.confirm({
      title: 'Clear sent history?',
      okText: `Delete ${totalBroadcasts.toLocaleString()} broadcast${totalBroadcasts === 1 ? '' : 's'}`,
      okButtonProps: { danger: true },
      cancelText: 'Keep them',
      content: (
        <span>
          This permanently removes every entry from this page. It does not recall any
          notification already delivered, and it does not erase the audit trail — each
          removal is recorded in the Audit Trail.
        </span>
      ),
      onOk: async () => {
        setClearing(true)
        try {
          const { data, error } = await supabaseClient.rpc('admin_clear_push_broadcasts')
          if (error) throw error
          const removed = Number(data ?? 0)
          message.success(`Cleared ${removed.toLocaleString()} broadcast${removed === 1 ? '' : 's'}`)
          // Refresh BOTH: the list and the summary view behind the KPI deck,
          // otherwise the cards keep showing the pre-clear totals.
          invalidate({ resource: 'push_broadcasts', invalidates: ['list'] })
          invalidate({ resource: 'admin_push_broadcasts_summary', invalidates: ['list'] })
        } catch (e) {
          message.error(`Couldn't clear history: ${e instanceof Error ? e.message : String(e)}`)
        } finally {
          setClearing(false)
        }
      },
    })
  }

  const stats: Stat[] = [
    { label: 'Total Broadcasts', value: totalBroadcasts.toLocaleString(), icon: Bell, tone: 'jaguar' },
    { label: 'Successfully Sent', value: sentCount.toLocaleString(), icon: Send, tone: sentCount > 0 ? 'green' : 'neutral' },
    { label: 'Total Recipients', value: totalRecipients.toLocaleString(), icon: UsersRound, tone: 'gold' },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Messaging · Push',
        description:
          'A complete log of every push broadcast dispatched to members — including audience targeting, recipient counts, and delivery status. Newest broadcasts appear first.',
        actions:
          rows.length > 0 ? (
            <Button danger onClick={clearHistory} loading={clearing}>
              Clear history
            </Button>
          ) : null,
      }}
      stats={stats}
      statColumns={3}
    >
      <SectionCard title="Sent History" sub="All broadcasts · newest first">
        {rows.length === 0 ? (
          <EmptyHint>No push broadcasts have been sent yet.</EmptyHint>
        ) : (
          <div>
            {rows.map((b, i) => {
              const [statusFg, statusBg] = statusColor(b.status)
              const audienceLabel = fmtAudience(b)
              return (
                <div
                  key={b.id}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 14,
                    padding: '14px 0',
                    borderBottom: i < rows.length - 1 ? '1px solid ' + brand.cardLine : 'none',
                  }}
                >
                  {/* Status pill */}
                  <div style={{ flexShrink: 0, paddingTop: 2 }}>
                    <span
                      style={{
                        display: 'inline-block',
                        fontFamily: brand.fSans,
                        fontWeight: 700,
                        fontSize: 11,
                        color: statusFg,
                        background: statusBg,
                        padding: '3px 10px',
                        borderRadius: 999,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {b.status}
                    </span>
                  </div>

                  {/* Main content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={mono}>{b.title}</span>
                      <Pill tone="neutral">{audienceLabel}</Pill>
                    </div>
                    <div
                      style={{
                        fontFamily: brand.fSans,
                        fontWeight: 500,
                        fontSize: 13,
                        color: brand.sub,
                        marginTop: 3,
                        lineHeight: 1.45,
                      }}
                    >
                      {truncate(b.body)}
                    </div>
                    {b.recipient_count != null && (
                      <div
                        style={{
                          fontFamily: brand.fSans,
                          fontWeight: 600,
                          fontSize: 11.5,
                          color: brand.jaguar,
                          marginTop: 4,
                        }}
                      >
                        {b.recipient_count.toLocaleString()} recipient{b.recipient_count !== 1 ? 's' : ''}
                      </div>
                    )}
                  </div>

                  {/* Timestamp */}
                  <span
                    style={{
                      fontFamily: brand.fSans,
                      fontWeight: 500,
                      fontSize: 11.5,
                      color: brand.sub,
                      flexShrink: 0,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {fmtWhen(b.created_at)}
                  </span>
                </div>
              )
            })}
          </div>
        )}
      </SectionCard>
    </ListPage>
  )
}
